/* eslint-disable ngrx/avoid-mapping-selectors */
/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get, some as _some } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import {
  BillAccountActions,
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { OneTimePayFeatureFacade } from '@amfam/billing/one-time-pay/feature';
import {
  PendingPayment,
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import { PendingRegistration } from '@amfam/billing/registration/data-access';
import { AnalyticsActions, AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillingAnalytics,
  BillingPaymentPaths,
  MultiAutopaySetupAnalytics,
  ONLINE_BILLING,
  SilentRegistrationPayload
} from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService
} from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-get-started',
  templateUrl: './bill-account-get-started.component.html',
  styleUrls: ['./bill-account-get-started.component.scss'],
  standalone: false
})
export class BillAccountGetStartedComponent implements OnInit {
  @Input() action: string;
  @Input() pendingPayments$: Observable<{ [id: string]: PendingPayment }>;
  @Input() pendingPayments: PendingPayment[];
  @Input() pendingRegistrations: PendingRegistration[];
  @Input() billacountsEligibleForAutoPay$: Observable<string[]>;
  @Input() hasPastDue: boolean;
  @Output() silentRegistrationFailureEvent = new EventEmitter<BillingNotificationsState[]>();
  public multiAutopayBillingSectionAnalytic = MultiAutopaySetupAnalytics.clickMultiAutopaySetup;

  getStartedPaymentBtnContent: string;
  pastDueEnabled: boolean;
  showSchedluePaymentBtn$: Observable<boolean>;

  constructor(
    private utilService: UtilService,
    public spinner: LoadingSpinnerService,
    private store: Store,
    private copyService: CopyService,
    private feature: FeatureFlagService,
    private analytic: AnalyticsFacade,
    private applicationService: ApplicationService,
    private autoPayUtil: AutoPayUtilService,
    private oneTimePayFacade: OneTimePayFeatureFacade
  ) {}

  ngOnInit() {
    this.pastDueEnabled = this.feature.isEnabled('classic_past_due');
    this.getStartedPaymentBtnContent = this.copyService.getCopy('billing.getStartedPaymentButton');

    if (this.pastDueEnabled && this.hasPastDue) {
      if (!this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) {
        this.analytic.trackPage(BillingAnalytics.pageOverviewPastDue);
      } else {
        this.store.dispatch(
          AnalyticsActions.sendAnalytic({
            options: {
              page: BillingAnalytics.pageAdminOverviewPastDue
            }
          })
        );
      }
    }
    this.showSchedluePaymentBtn$ = this.oneTimePayFacade.showSchedulePaymentButton();
  }

  registerAccounts() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_REGISTRATION]
      })
    );
  }

  registerAndNavigate(isSetupAutopay = false, isScheduledPayment = false) {
    if (isSetupAutopay) {
      // AUTOPAY ENTRY POINT
      this.billacountsEligibleForAutoPay$.pipe(take(1)).subscribe(billAccounts => {
        const selectedBillAccountNumbers: string[] = billAccounts.filter(billAccount =>
          this.pendingPayments
            .map(pendingPayment => pendingPayment.billAccountNumber)
            .includes(billAccount)
        );
        this.autoPayUtil.routeToMultipleAutoPaySelection('billing', selectedBillAccountNumbers);
      });
    } else {
      if (_get(this.pendingRegistrations, 'length', 0) > 0) {
        this.spinner.start({ blockActions: true });
        const correlationId = this.utilService.generateId();
        const silentRegistrationPayload: SilentRegistrationPayload[] = [];
        this.pendingRegistrations.forEach(pendingRegistration => {
          const payload: SilentRegistrationPayload = {
            billAccountNumber: pendingRegistration.billAccountNumber,
            correlationId: correlationId
          };
          silentRegistrationPayload.push(payload);
        });

        this.store.dispatch(
          BillAccountActions.billAccountSilentRegistration(silentRegistrationPayload)
        );

        this.store
          .select(BillAccountsSelectors.selectAllBillAccountsNotifications)
          .pipe(
            filter(billAccounts => !_some(billAccounts, { updatingRegistration: true })),
            map(billAccounts => {
              const filtered = billAccounts.filter(ba => ba.correlationId === correlationId);
              return filtered;
            }),
            filter(billAccounts => billAccounts.length === this.pendingRegistrations.length),
            take(1),
            withLatestFrom(this.store.select(BillAccountsSelectors.selectBillAccounts))
          )
          .subscribe(([billAccountsNotifications, billAccounts]) => {
            this.spinner.stop();

            billAccounts = billAccounts.filter(
              ba =>
                !!billAccountsNotifications.find(
                  ban => ban.billAccountNumber === ba.billAccountNumber
                )
            );

            // Only selected bill accounts are relevant.
            const selectedBillAccounts = billAccounts.filter(ba =>
              _some(this.pendingPayments, { billAccountNumber: ba.billAccountNumber })
            );

            // Remove pending payments for unregistered bill accounts because those are not allowed to make a payments.
            selectedBillAccounts
              .filter(ba => ba.billingMethod !== ONLINE_BILLING)
              .map(ba => {
                this.store.dispatch(
                  pendingPaymentActions.RemovePendingPaymentAction({
                    billAccountNumber: ba.billAccountNumber
                  })
                );
              });

            // Route to make a payment section if atleast one bill account is successfully registered.
            if (_some(selectedBillAccounts, ['billingMethod', ONLINE_BILLING])) {
              if (isScheduledPayment) {
                // ONE TIME PAYMENT ENTRYPOINT
                if (this.feature.isEnabled('simplified_payment')) {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                    })
                  );
                } else {
                  this.store
                    .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
                    .pipe(take(1))
                    .subscribe(billAccount => {
                      if (billAccount.length === 1) {
                        this.store.dispatch(
                          fromRouterActions.Go({
                            path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                          })
                        );
                      } else {
                        this.store.dispatch(
                          fromRouterActions.Go({
                            path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                          })
                        );
                      }
                    });
                }
              }
            } else {
              const unregisteredBillAccounts = selectedBillAccounts.filter(
                ba => _get(ba, 'billingMethod') !== ONLINE_BILLING
              );

              this.silentRegistrationFailureEvent.emit(
                billAccountsNotifications.filter(
                  ban =>
                    !!unregisteredBillAccounts.find(
                      ba => ba.billAccountNumber === ban.billAccountNumber
                    )
                )
              );
            }
          });
      } else {
        if (isScheduledPayment) {
          // ONE TIME PAYMENT ENTRYPOINT
          if (this.feature.isEnabled('simplified_payment')) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          } else {
            {
              this.store
                .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
                .pipe(take(1))
                .subscribe(billAccounts => {
                  if (billAccounts.length === 1) {
                    this.store.dispatch(
                      fromRouterActions.Go({
                        path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                      })
                    );
                  } else {
                    this.store.dispatch(
                      fromRouterActions.Go({
                        path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                      })
                    );
                  }
                });
            }
          }
        }
      }
    }
  }
}
